/**
 * Procurement category parsing and matching utilities.
 * Supports multi-category deals where deal.category contains pipe-separated values
 * (e.g. "Academic Integrity / Plagiarism Detection | AI & Virtual Assistants").
 */

import { toOfficialCategoryName, isOfficialCategoryName } from '@/utils/edtechCategoryNames';

/** Split deal.category by | (do not split on / — it's part of category names like "Academic Integrity / Plagiarism Detection") */
export function splitDealCategoryString(categoryValue) {
  if (!categoryValue) return [];
  return String(categoryValue)
    .split('|')
    .map((part) => part.trim())
    .filter(Boolean);
}

/** Normalize a single category value to official name */
export function normalizeCategoryValue(categoryValue) {
  const official = toOfficialCategoryName(categoryValue) || categoryValue;
  return String(official || '').trim();
}

function matchedCategoriesFromDeal(deal) {
  return (deal?.matched_categories || [])
    .map((c) => (typeof c === 'string' ? c : c?.name || c?.category || ''))
    .map(normalizeCategoryValue)
    .filter(Boolean);
}

/** Categories from deal.category only (pipe-separated); excludes matched_categories. */
export function getDealCategoriesFromCategoryField(deal) {
  if (!deal?.category) return [];
  return [...new Set(splitDealCategoryString(deal.category).map(normalizeCategoryValue).filter(Boolean))];
}

/** Get parsed array of categories from a deal (category + matched_categories) */
export function getDealCategories(deal) {
  const fromCategory = getDealCategoriesFromCategoryField(deal);
  const fromMatched = matchedCategoriesFromDeal(deal);
  if (!fromCategory.length && !fromMatched.length) return [];
  return [...new Set([...fromCategory, ...fromMatched])];
}

/**
 * Categories of a deal that may be offered as a FILTER OPTION.
 *
 * [2026-10-17] Deliberately NOT getDealCategories(). The BE began projecting `matched_categories`
 * on live deals so bundled multi-category deals are retrievable, and the option list is built from
 * the loaded deals — so every value in that array became a tickable filter entry. Measured on the
 * 9,224 live rows: the Category dropdown went from 70 options to 414, the new ones being raw
 * taxonomy keys ('edtech_core'), vendor names ('turnitin'), and whole sentences carried in that
 * column by older loaders.
 *
 * So: the PRIMARY `category` field keeps contributing whatever it holds — unchanged behaviour, and
 * it is the column the loaders validate — while `matched_categories` contributes only values that
 * resolve into the official taxonomy. This narrows the OPTION LIST only; matching still reads the
 * full union via getDealCategories(), so a bundled deal is still retrieved by its canonical
 * categories. A value that is not offered cannot be ticked, so nothing becomes unreachable.
 */
export function getDealOptionCategories(deal) {
  const fromCategory = getDealCategoriesFromCategoryField(deal);
  const fromMatched = matchedCategoriesFromDeal(deal).filter(isOfficialCategoryName);
  if (!fromCategory.length && !fromMatched.length) return [];
  return [...new Set([...fromCategory, ...fromMatched])];
}

/** Display string from deal.category only (Contract Intelligence table column). */
export function getDealCategoryDisplayFromCategoryField(deal) {
  return getDealCategoriesFromCategoryField(deal).join(' | ');
}

/** Get display string for deal categories (joined with | ) */
export function getDealCategoryDisplay(deal) {
  const cats = getDealCategories(deal);
  return cats.join(' | ');
}

/** Searchable category text for filtering */
export function getSearchableCategoryText(deal) {
  return getDealCategories(deal).join(' | ');
}

/** Match selected categories against deal — returns true if any deal category is in selected */
export function categoryMatches(selected, dealOrCategory) {
  const selectedNorm = new Set((selected || []).map((c) => normalizeCategoryValue(c)));

  const dealCats =
    typeof dealOrCategory === 'string'
      ? splitDealCategoryString(dealOrCategory).map(normalizeCategoryValue).filter(Boolean)
      : getDealCategories(dealOrCategory);

  return dealCats.some((c) => selectedNorm.has(c));
}

/** Closed deals: primary category label or any matched_categories taxonomy key. */
export function closedCategoryMatch(selected, deal) {
  if (!selected?.length) return true;
  const selectedNorm = new Set((selected || []).map((c) => normalizeCategoryValue(c)));
  const dealCats = getDealCategories(deal);
  return dealCats.some((c) => selectedNorm.has(c));
}

/** Disclosure when one contract covers more than one category. Value is not split. */
export function bundledContractNote(deal) {
  const labels = getDealCategories(deal);
  if (labels.length <= 1) return null;
  return `Bundled contract — covers ${labels.join(', ')}. Value shown is the full contract.`;
}

/** Stable key for deduplication — one deal = one row */
export function getDealKey(d) {
  return (
    d?.id ||
    d?.deal_id ||
    d?.tender_id ||
    [
      (d?.title || '').trim(),
      (d?.account_name || '').trim(),
      (d?.country || '').trim(),
      (d?.deadline || '').trim(),
      String(d?.predicted_expiry || d?.predicted_expiry_date || '').trim(),
      (d?.supplier || '').trim(),
    ].join('|')
  );
}

/** Deduplicate deals by stable key — prevents double counting when multiple categories selected */
export function dedupeDealsByKey(deals) {
  const map = new Map();
  (deals || []).forEach((deal) => {
    map.set(getDealKey(deal), deal);
  });
  return Array.from(map.values());
}
